import {
  type GetAccountsPaymentMethodsByAccountIdResponse,
  useGetAccountsPaymentMethodsByAccountId,
} from "@birdwatch/client";

/** One saved card or bank account, exactly as the platform returns it. */
export type PresalePaymentMethod =
  GetAccountsPaymentMethodsByAccountIdResponse[number];

/**
 * The account's saved payment methods — the list the buyer picks from.
 *
 * Account-scoped rather than intent-scoped: the intent records WHICH method was chosen
 * (`payload.paymentMethodId`, which the checkout page writes on selection), not what the
 * account has available. The `accountId` comes off the purchase intent the checkout page
 * already reads.
 */
export function usePresalePaymentMethods(accountId: string | undefined) {
  const query = useGetAccountsPaymentMethodsByAccountId({
    accountId: accountId ?? "",
  }).get({ enabled: Boolean(accountId) });

  return {
    paymentMethods: query.data ?? [],
    isLoading: query.isLoading,
    /**
     * Refetches and returns the FRESH list.
     *
     * The hook's own `data` is unwrapped, but `query.refetch()` resolves the raw
     * `{ data }` envelope — so the unwrapping lives here rather than leaking that
     * detail to every caller. Returning the list is what lets a caller act on a card
     * that is not in the array its render closed over.
     */
    refetch: async (): Promise<PresalePaymentMethod[]> => {
      const result = await query.refetch();
      return result.data?.data ?? [];
    },
  };
}
